import { defineStore } from "pinia";
import { ref } from "vue";
import { companyOrderApi } from "./companyOrderApi";
import {
  CompanyOrder,
  CompanyOrderUpdatePayload,
  OrderStatus,
} from "./companyOrderTypes";

export const useCompanyOrderStore = defineStore("companyOrder", () => {
  const orders = ref<CompanyOrder[]>([]);
  const selectedOrder = ref<CompanyOrder | null>(null);
  const isLoading = ref(false);
  const isConfirming = ref<number | null>(null);
  const isUpdatingStatus = ref<number | null>(null);
  const isBatchProcessing = ref(false);
  const batchProcessingOrderId = ref<number | null>(null);
  let ordersRequestSequence = 0;

  const fetchOrders = async (
    companyIds: number[],
    dateFrom: string | null = null,
    dateBefore: string | null = null
  ) => {
    if (companyIds.length === 0) {
      ordersRequestSequence++;
      orders.value = [];
      isLoading.value = false;
      return;
    }
    await loadLatestOrders(() =>
      companyOrderApi.listCompanyOrders({
        company_ids: companyIds,
        date_from: dateFrom,
        date_before: dateBefore,
      })
    );
  };

  const fetchAdminNonCompletedOrders = (
    dateFrom: string | null = null,
    dateBefore: string | null = null
  ) =>
    loadLatestOrders(() =>
      companyOrderApi.listAdminNonCompletedCompanyOrders({
        date_from: dateFrom,
        date_before: dateBefore,
      })
    );

  const loadLatestOrders = async (
    request: () => Promise<{ data: { orders: CompanyOrder[] } }>
  ) => {
    const sequence = ++ordersRequestSequence;
    isLoading.value = true;
    try {
      const response = await request();
      // NOTE: companies and dates can change while a request is in flight; only the latest may land.
      if (sequence === ordersRequestSequence)
        orders.value = response.data.orders;
    } finally {
      if (sequence === ordersRequestSequence) isLoading.value = false;
    }
  };

  /**
   * Replaces the order's list row (and the selected order) with the server's, e.g. the order an action
   * route has just returned. A whole row, never a patch: its phase and allowed actions change with its status.
   */
  const replaceOrder = (order: CompanyOrder) => {
    orders.value = orders.value.map((existing: CompanyOrder) =>
      existing.id === order.id ? order : existing
    );
    if (selectedOrder.value?.id === order.id) {
      selectedOrder.value = order;
    }
  };

  const confirmOrCancel = async (
    orderId: number,
    apiCall: () => Promise<{ data: { order: CompanyOrder } }>
  ) => {
    isConfirming.value = orderId;
    try {
      replaceOrder((await apiCall()).data.order);
    } finally {
      isConfirming.value = null;
    }
  };

  const confirmOrder = (orderId: number) =>
    confirmOrCancel(orderId, () => companyOrderApi.confirmOrder(orderId));

  const cancelOrder = (orderId: number) =>
    confirmOrCancel(orderId, () => companyOrderApi.cancelOrder(orderId));

  const updateOrder = async (
    orderId: number,
    payload: CompanyOrderUpdatePayload
  ) => {
    isUpdatingStatus.value = orderId;
    try {
      const response = await companyOrderApi.updateOrder(orderId, payload);
      replaceOrder(response.data.order);
      return response.data;
    } finally {
      isUpdatingStatus.value = null;
    }
  };

  const adminUpdateStatus = async (
    orderId: number,
    status: "COMPLETED" | "FAILED" | "CANCELLED"
  ) => {
    isUpdatingStatus.value = orderId;
    try {
      const response = await companyOrderApi.adminUpdateStatus(orderId, status);
      replaceOrder(response.data.data.order);
    } finally {
      isUpdatingStatus.value = null;
    }
  };

  const adminRetryOrder = async (orderId: number) => {
    isUpdatingStatus.value = orderId;
    try {
      const response = await companyOrderApi.adminRetryOrder(orderId);
      replaceOrder(response.data.data.order);
    } finally {
      isUpdatingStatus.value = null;
    }
  };

  const setSelectedOrder = (order: CompanyOrder | null) => {
    selectedOrder.value = order;
  };

  const processBatchOrders = async (
    visibleOrders: CompanyOrder[],
    processOrder: (order: CompanyOrder) => Promise<void>,
    errorLogPrefix: string
  ) => {
    const waitingOrders = visibleOrders.filter(
      (order) => order.status === OrderStatus.WAITING_CONFIRMATION
    );

    if (waitingOrders.length === 0) {
      return { success: 0, failed: 0 };
    }

    isBatchProcessing.value = true;
    let successCount = 0;
    let failedCount = 0;

    for (const order of waitingOrders) {
      try {
        batchProcessingOrderId.value = order.id;
        await processOrder(order);
        successCount++;
      } catch (err) {
        failedCount++;
        console.error(`${errorLogPrefix} ${order.id}:`, err);
      }
    }

    batchProcessingOrderId.value = null;
    isBatchProcessing.value = false;

    return { success: successCount, failed: failedCount };
  };

  const confirmAllWaitingOrders = async (visibleOrders: CompanyOrder[]) => {
    return processBatchOrders(
      visibleOrders,
      (order) => confirmOrder(order.id),
      "Failed to confirm order"
    );
  };

  const cancelAllWaitingOrders = async (visibleOrders: CompanyOrder[]) => {
    return processBatchOrders(
      visibleOrders,
      (order) => cancelOrder(order.id),
      "Failed to cancel order"
    );
  };

  const rejectAllWaitingOrders = async (visibleOrders: CompanyOrder[]) => {
    return processBatchOrders(
      visibleOrders,
      (order) => adminUpdateStatus(order.id, "CANCELLED"),
      "Failed to reject order"
    );
  };

  return {
    orders,
    selectedOrder,
    isLoading,
    isConfirming,
    isUpdatingStatus,
    isBatchProcessing,
    batchProcessingOrderId,
    fetchOrders,
    fetchAdminNonCompletedOrders,
    confirmOrder,
    cancelOrder,
    updateOrder,
    adminUpdateStatus,
    adminRetryOrder,
    replaceOrder,
    setSelectedOrder,
    confirmAllWaitingOrders,
    cancelAllWaitingOrders,
    rejectAllWaitingOrders,
  };
});
